import { RoleEnumDto, UserModelDto } from '.generated/api';
import { organizationActions } from 'actions/organizationActions';
import { ApiService } from 'services/apiService';
import { notificationActions } from 'actions/notificationActions';
import { AxiosError } from 'axios';
import { ApplicationState, AppDispatch } from 'store';

function clearDepartment() {
  return {
    type: 'GET_DEPARTMENT_SUCCESS',
    department: undefined,
  };
}

function getDepartmentById(departmentId: string) {
  return (dispatch: AppDispatch) => {
    ApiService.getDepartmentById(departmentId)
      .then((response) => {
        dispatch({
          type: 'GET_DEPARTMENT_SUCCESS',
          department: response.data,
        });
      })
      .catch((error) => {
        dispatch({
          type: 'GET_DEPARTMENT_ERROR',
          error: error.response?.data?.errorMessage,
        });
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

function createDept(name: string, organizationId: string) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.createDeptment(name, organizationId)
      .then((response) => {
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_DEPARTMENT_SUCCESS',
          department: response.data,
        });
        dispatch({ type: 'SET_STEP', step: 2 });
        dispatch(organizationActions.getOrganizationById(organizationId));
      })
      .catch((error) => {
        // TODO MAL: use error code once that exists
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_DEPARTMENT_FAILURE',
          error: error.response?.data?.errorMessage,
        });
      });
  };
}

function updateDepartmentInfo(
  departmentId: string,
  departmentName: string,
  departmentNumber?: number,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.updateDepartmentInfo(
      departmentId,
      departmentName,
      departmentNumber,
    )
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
        if (callback) {
          callback();
        }
      })
      .catch((error) => {
        if (callback) {
          callback(error);
        } else {
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function addAdminToDept(
  organizationId: string,
  departmentId: string,
  email: string,
  name: string,
  phone: string,
) {
  return (dispatch: AppDispatch) => {
    dispatch({ type: 'SET_IS_PROCESSING', isProcessing: true });

    ApiService.addDepartmentAdmin(departmentId, email, name, phone)
      .then(() => {
        dispatch(organizationActions.getOrganizationById(organizationId));
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({ type: 'SET_FLOW_COMPLETED' });
      })
      .catch((error) => {
        // TODO MAL: use error code once that exists
        dispatch({ type: 'SET_IS_PROCESSING', isProcessing: false });
        dispatch({
          type: 'CREATE_DEPARTMENT_FAILURE',
          error: error.response?.data?.errorMessage,
        });
      });
  };
}

function addUserToDepartment(
  departmentId: string,
  user: UserModelDto,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.addUserToDepartment(departmentId, user)
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function editUserInDept(
  departmentId: string,
  email: string,
  name: string,
  phone: string,
  roles: RoleEnumDto[],
  enableAutoEmails: boolean,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.editUserInDeptment(
      departmentId,
      email,
      name,
      phone,
      roles,
      enableAutoEmails,
    )
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function addContact(
  departmentId: string,
  name: string | undefined,
  email: string | undefined,
  phone: string | undefined,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.addDepartmentContact(departmentId, name, email, phone)
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function editContact(
  departmentId: string,
  contactId: string,
  name: string | undefined,
  email: string | undefined,
  phone: string | undefined,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.updateDepartmentContact(
      departmentId,
      contactId,
      name,
      email,
      phone,
    )
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function updateCustomerNumber(
  departmentId: string,
  customerNumber: string | null,
  callback?: (error?: AxiosError) => void,
) {
  return (dispatch: AppDispatch, getState: () => ApplicationState) => {
    ApiService.updateDepartmentCustomerNumber(departmentId, { customerNumber })
      .then(() => {
        const department = getState().department.department;
        if (department) {
          dispatch({
            type: 'GET_DEPARTMENT_SUCCESS',
            department: { ...department, customerNumber: customerNumber },
          });
        }
        callback && callback();
      })
      .catch((error) => {
        if (callback) {
          callback(error);
        } else {
          dispatch(notificationActions.enqueueErrorResponse(error));
        }
      });
  };
}

function deleteContact(
  departmentId: string,
  contactId: string,
  callback: (error?: string) => void,
) {
  return (dispatch: AppDispatch) => {
    ApiService.deleteDepartmentContact(departmentId, contactId)
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
        callback();
      })
      .catch((error) => {
        // TODO MAL: handle error in a more general way instead of using a callback
        callback(error.response?.data?.errorMessage ?? error.message);
      });
  };
}

function addEmailToDepartmentMailingList(departmentId: string, email: string) {
  return (dispatch: AppDispatch) => {
    ApiService.addEmailToDepartmentMailingList(departmentId, email)
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
      })
      .catch((error) => {
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

function removeEmailFromDepartmentMailingList(
  departmentId: string,
  email: string,
) {
  return (dispatch: AppDispatch) => {
    ApiService.removeEmailFromDepartmentMailingList(departmentId, email)
      .then(() => {
        dispatch(departmentActions.getDepartmentById(departmentId));
      })
      .catch((error) => {
        dispatch(notificationActions.enqueueErrorResponse(error));
      });
  };
}

export const departmentActions = {
  getDepartmentById,
  clearDepartment,
  createDept,
  updateDepartmentInfo,
  updateCustomerNumber,
  addAdminToDept,
  addUserToDepartment,
  editUserInDept,
  addContact,
  editContact,
  deleteContact,
  addEmailToDepartmentMailingList,
  removeEmailFromDepartmentMailingList,
};
